/**
 * Mirrors GET t/:slug/api/v1/finance/accounts (+ :id) — see
 * apps/api/src/accounting/coa.controller.ts. Create (`POST
 * .../finance/accounts`) and set-postable (`PATCH .../finance/accounts/:ref`,
 * `:ref` the account **code**, never the raw `accountId`) are real —
 * apps/api/src/finance/accounts.controller.ts, `PERMISSION_COA_CREATE` /
 * `PERMISSION_COA_UPDATE` from `@sakhtar/module-accounting`. The previous
 * "read-only, no permission key exists yet" note here was stale.
 */
export interface AccountListItem {
  readonly accountId: string;
  readonly parentAccountId: string | null;
  readonly code: string;
  readonly name: string;
  readonly levelId: string;
  readonly isPostable: boolean;
  readonly status: string;
  /** Account nature (ماهیت حساب). Optional for older servers. */
  readonly normalBalance?: AccountNormalBalance;
}

export type AccountNormalBalance = 'debit' | 'credit' | 'either';

export const NORMAL_BALANCE_LABEL: Record<AccountNormalBalance, string> = {
  debit: 'بدهکار',
  credit: 'بستانکار',
  either: 'مهم نیست',
};

export interface AccountListResponse {
  readonly items: readonly AccountListItem[];
}

/**
 * Journal mirrors — list/detail come from
 * apps/api/src/accounting/journal.controller.ts (`GET
 * /accounting/journal-entries`), lifecycle mutations
 * (create/submit/approve/post) from
 * apps/api/src/finance/journal.controller.ts (`POST
 * /finance/journals/**`), reverse from the accounting controller. Two
 * different controllers, same underlying journal — see the route
 * inventory's Finance section.
 */
export type JournalStatus = 'draft' | 'submitted' | 'approved' | 'posted' | 'tombstoned';

export const JOURNAL_STATUS_LABEL: Record<JournalStatus, string> = {
  draft: 'پیش‌نویس',
  submitted: 'ارسال‌شده',
  approved: 'تأییدشده',
  // v2 lifecycle wording: a posted journal is final (immutable; corrected only by reversal).
  posted: 'ثبت قطعی',
  tombstoned: 'باطل‌شده',
};

export interface JournalLine {
  readonly journalLineId: string;
  readonly rowNumber: number;
  readonly accountId: string;
  readonly accountCode: string | null;
  readonly accountName: string | null;
  readonly description: string | null;
  readonly debitRial: string;
  readonly creditRial: string;
  readonly dimensions?: readonly JournalLineDimension[];
}

export interface JournalLineDimension {
  readonly dimensionId: string;
  readonly dimensionName: string | null;
  readonly dimensionValueId: string;
  readonly valueCode: string | null;
  readonly valueName: string | null;
}

export interface JournalEntry {
  readonly journalId: string;
  readonly provisionalNumber: string;
  readonly officialDocumentNumber: string | null;
  readonly businessDate: string;
  readonly status: JournalStatus;
  readonly journalType: string;
  readonly description: string | null;
  readonly sourceModule: string;
  readonly sourceType: string;
  readonly sourceReference: string | null;
  readonly sourceRecomputationPending: boolean;
  readonly backdatingReason: string | null;
  readonly backdatingAuthorizedBy: string | null;
  readonly debitTotal: string;
  readonly creditTotal: string;
  readonly reversalJournalId: string | null;
  readonly lines?: readonly JournalLine[];
}

export interface JournalListResponse {
  readonly items: readonly JournalEntry[];
  readonly total: number;
}

export interface FiscalPeriod {
  readonly fiscalPeriodId: string;
  readonly fiscalYearId: string;
  readonly name: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly status: string;
  readonly postingState: 'open' | 'closed';
  readonly closedAt: string | null;
  readonly closedBy: string | null;
}

export interface FiscalPeriodListResponse {
  readonly items: readonly FiscalPeriod[];
}

export interface FinanceOverviewJournalRow {
  readonly journalId: string;
  readonly provisionalNumber: string;
  readonly officialDocumentNumber?: string;
  readonly businessDate: string;
  readonly statusLabel: string;
  readonly debitTotalRial: string;
}

export interface FinanceOverviewResponse {
  /** ERP-TBD-1 — false until a chart of accounts exists; finance writes then return COA_NOT_CONFIGURED. Optional for older servers. */
  readonly chartOfAccountsConfigured?: boolean;
  readonly summary: {
    readonly provisionalJournalCount: number;
    readonly postedJournalCount: number;
    readonly provisionalLineCount: number;
    readonly officialLineCount: number;
  };
  readonly provisionalJournals: readonly FinanceOverviewJournalRow[];
  readonly postedJournals: readonly FinanceOverviewJournalRow[];
}

/** Mirrors apps/api/src/finance/subledger-serialize.ts */
export type OpenItemKind = 'ar' | 'ap';
export type OpenItemStatus = 'draft' | 'open' | 'partial' | 'settled' | 'reversed';
export type OpenItemSourceType = 'manual' | 'sales_handoff' | 'procurement_handoff' | 'reversal';

export interface OpenItem {
  readonly openItemId: string;
  readonly kind: OpenItemKind;
  readonly partyId: string;
  readonly sourceType: OpenItemSourceType | string;
  readonly sourceReference: string;
  readonly businessDate: string;
  readonly dueDate: string;
  readonly originalAmountRial: string;
  readonly openBalanceRial: string;
  readonly status: OpenItemStatus | string;
  readonly statusLabel: string;
  readonly originatingJournalId: string;
  readonly controlAccountId: string;
  readonly counterpartAccountId: string;
  readonly vatAccountId: string | null;
  readonly taxId: string | null;
  readonly taxableBaseRial: string;
  readonly vatAmountRial: string;
  /** Present on the detail response for itemised manual invoices. */
  readonly lines?: readonly OpenItemLine[];
}

export interface OpenItemLine {
  readonly openItemLineId: string;
  readonly rowNumber: number;
  readonly lineKind: 'item' | 'charge';
  readonly chargeType: OpenItemChargeType | null;
  readonly productId: string | null;
  readonly description: string;
  readonly quantityNum: string;
  readonly quantityDen: string;
  readonly unitPriceRial: string;
  readonly grossAmountRial: string;
  readonly lineDiscountRial: string;
  readonly documentDiscountRial: string;
  readonly taxableBaseRial: string;
  readonly vatAmountRial: string;
  readonly totalAmountRial: string;
  readonly accountId: string;
}

export type OpenItemChargeType = 'freight' | 'warehousing' | 'insurance' | 'other';

export const CHARGE_TYPE_LABEL: Record<OpenItemChargeType, string> = {
  freight: 'کرایه حمل',
  warehousing: 'انبارداری',
  insurance: 'بیمه',
  other: 'سایر هزینه‌های جانبی',
};

export interface OpenItemListResponse {
  readonly items: readonly OpenItem[];
}

export interface AgingBucket {
  readonly bucket: string;
  readonly itemCount: number;
  readonly openBalanceRial: string;
}

export interface AgingItem extends OpenItem {
  readonly daysPastDue: number;
  readonly bucket: string;
}

export interface AgingResponse {
  readonly asOfDate: string;
  readonly items: readonly AgingItem[];
  readonly buckets: readonly AgingBucket[];
}

export type TreasuryAccountKind = 'cash' | 'bank';
export type TreasuryTxnKind = 'receipt' | 'payment';
export type TreasuryTxnStatus = 'draft' | 'posted' | 'reversed';

export interface TreasuryAccount {
  readonly treasuryAccountId: string;
  readonly code: string;
  readonly name: string;
  readonly kind: TreasuryAccountKind | string;
  readonly kindLabel: string;
  readonly glAccountId: string;
  readonly bankAccountId: string | null;
  readonly status: string;
}

export interface TreasuryAccountListResponse {
  readonly items: readonly TreasuryAccount[];
}

export interface TreasuryAllocation {
  readonly allocationId: string;
  readonly openItemKind: OpenItemKind | string;
  readonly openItemId: string;
  readonly amountRial: string;
}

export interface TreasurySettlement {
  readonly settlementId: string;
  readonly treasuryTransactionId: string;
  readonly originatingJournalId: string;
  readonly status: string;
  readonly allocations: readonly TreasuryAllocation[];
}

export interface TreasuryTransaction {
  readonly treasuryTransactionId: string;
  readonly kind: TreasuryTxnKind | string;
  readonly kindLabel: string;
  readonly treasuryAccountId: string;
  readonly partyId: string | null;
  readonly sourceReference: string;
  readonly businessDate: string;
  readonly amountRial: string;
  readonly status: TreasuryTxnStatus | string;
  readonly originatingJournalId: string;
  readonly journalNumber?: string | null;
  readonly description?: string | null;
  readonly counterpartAccountId: string | null;
  readonly settlementMethod?: TreasurySettlementMethod | null;
  readonly bankReference?: string | null;
  readonly journal?: JournalEntry;
  readonly settlement?: TreasurySettlement | null;
}

export interface TreasuryTransactionListResponse {
  readonly items: readonly TreasuryTransaction[];
}

export type VatDirection = 'input' | 'output';
export type VatSourceKind = 'ar_open_item' | 'ap_open_item';
export type VatEntryStatus = 'draft' | 'posted' | 'reversed';

export interface VatEntry {
  readonly vatEntryId: string;
  readonly sourceKind: VatSourceKind | string;
  readonly sourceId: string;
  readonly taxId: string;
  readonly direction: VatDirection | string;
  readonly directionLabel: string;
  readonly taxableBaseRial: string;
  readonly vatAmountRial: string;
  readonly businessDate: string;
  readonly journalId: string;
  readonly status: VatEntryStatus | string;
}

export interface VatEntryListResponse {
  readonly items: readonly VatEntry[];
}

/** `GET .../finance/reports/trial-balance` — apps/api/src/finance/reports.controller.ts. Posted-only, official. */
export interface TrialBalanceRow {
  readonly accountRef: string;
  readonly accountName: string;
  readonly debitMovementRial: string;
  readonly creditMovementRial: string;
  readonly debitBalanceRial: string;
  readonly creditBalanceRial: string;
}

export interface TrialBalanceTotals {
  readonly debitMovementRial: string;
  readonly creditMovementRial: string;
  readonly debitBalanceRial: string;
  readonly creditBalanceRial: string;
}

export interface TrialBalanceResponse {
  readonly authority: string;
  readonly kind: string;
  readonly asOfDate: string;
  readonly fiscalPeriodCode: string | null;
  readonly rows: readonly TrialBalanceRow[];
  readonly totals: TrialBalanceTotals;
  readonly balanced: boolean;
}

export type TreasurySettlementMethod = 'cash' | 'pos' | 'bank_transfer' | 'card_to_card' | 'cheque';

export const SETTLEMENT_METHOD_LABEL: Record<TreasurySettlementMethod, string> = {
  cash: 'نقد',
  pos: 'کارت‌خوان (پوز)',
  bank_transfer: 'حواله بانکی (ساتنا/پایا)',
  card_to_card: 'کارت به کارت',
  cheque: 'چک صیادی',
};

export type ChequeDirection = 'inward' | 'outward';
export type ChequeStatus =
  | 'in_safe'
  | 'in_collection'
  | 'cleared'
  | 'bounced'
  | 'returned'
  | 'issued'
  | 'delivered'
  | 'voided';

export const CHEQUE_DIRECTION_LABEL: Record<ChequeDirection, string> = {
  inward: 'دریافتی',
  outward: 'پرداختی',
};

export const CHEQUE_STATUS_LABEL: Record<ChequeStatus, string> = {
  in_safe: 'نزد صندوق',
  in_collection: 'در جریان وصول',
  cleared: 'وصول‌شده / پاس‌شده',
  bounced: 'برگشتی',
  returned: 'عودت‌داده‌شده',
  issued: 'صادرشده',
  delivered: 'تحویل ذینفع',
  voided: 'باطل‌شده',
};

export interface ChequeItem {
  readonly chequeId: string;
  readonly direction: ChequeDirection;
  readonly sayadId: string;
  readonly serialNumber: string;
  readonly series: string | null;
  readonly bankName: string;
  readonly branchName: string | null;
  readonly branchCode: string | null;
  readonly accountNumber: string | null;
  readonly iban: string | null;
  readonly amountRial: string;
  readonly amountWords: string | null;
  readonly issueDate: string;
  readonly dueDate: string;
  readonly drawerName: string | null;
  readonly drawerNationalId: string | null;
  readonly payeeName: string | null;
  readonly payeeNationalId: string | null;
  readonly partyId: string | null;
  readonly status: ChequeStatus;
  readonly currentTreasuryAccountId: string | null;
  readonly bankAccountId: string | null;
  readonly chequebookId: string | null;
  readonly imageUrl: string | null;
  readonly aiScanMetadata: unknown | null;
  readonly notes: string | null;
  readonly originatingJournalId: string | null;
  readonly latestJournalId: string | null;
  readonly createdAt: string;
}

export interface ChequeListResponse {
  readonly items: readonly ChequeItem[];
}

export interface ChequeTransitionItem {
  readonly transitionId: string;
  readonly fromStatus: string;
  readonly toStatus: string;
  readonly transitionDate: string;
  readonly journalId: string | null;
  readonly notes: string | null;
  readonly performedBy: string;
  readonly createdAt: string;
}

export interface ChequeDetailResponse {
  readonly cheque: ChequeItem;
  readonly transitions: readonly ChequeTransitionItem[];
}

export interface ChequeScanResult {
  readonly success: boolean;
  readonly confidence: number;
  readonly data: {
    readonly sayadId: string | null;
    readonly serialNumber: string | null;
    readonly series: string | null;
    readonly dueDateJalali: string | null;
    readonly dueDate: string | null;
    readonly amountRial: string | null;
    readonly amountWords: string | null;
    readonly bankName: string | null;
    readonly branchName: string | null;
    readonly branchCode: string | null;
    readonly accountNumber: string | null;
    readonly iban: string | null;
    readonly drawerName: string | null;
    readonly drawerNationalId: string | null;
    readonly payeeName: string | null;
    readonly payeeNationalId: string | null;
  };
  readonly fieldConfidence: Record<string, number>;
  readonly error?: string | null;
}

export interface ChequebookItem {
  readonly chequebookId: string;
  readonly bankAccountId: string;
  readonly series: string;
  readonly fromSerial: string;
  readonly toSerial: string;
  readonly leafCount: number;
  readonly usedCount: number;
  readonly voidedCount: number;
  readonly status: string;
  readonly createdAt: string;
}

export interface ChequebookListResponse {
  readonly items: readonly ChequebookItem[];
}

/** `GET/POST .../finance/treasury/bank-accounts` */
export interface BankAccountDetail {
  readonly bankAccountId: string;
  readonly code: string;
  readonly name: string;
  readonly status: string;
  readonly bankName: string | null;
  readonly branchName: string | null;
  readonly branchCode: string | null;
  readonly accountNumber: string | null;
  readonly iban: string | null;
  readonly cardNumber: string | null;
}

export interface BankAccountListResponse {
  readonly items: readonly BankAccountDetail[];
}

/** `GET .../finance/fiscal-years` */
export interface FiscalYear {
  readonly fiscalYearId: string;
  readonly code: string;
  readonly name: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly status: string;
  readonly periodCount: number;
}

export interface FiscalYearListResponse {
  readonly items: readonly FiscalYear[];
}

/** `GET .../finance/dimension-rules` */
export interface DimensionRule {
  readonly accountDimensionRuleId: string;
  readonly accountId: string;
  readonly accountRef: string;
  readonly dimensionId: string;
  readonly dimensionCode: string;
  readonly dimensionName: string;
  readonly required: boolean;
  readonly allowedPolicy: 'any' | 'bound_only';
  readonly status: 'active' | 'inactive';
}

export interface DimensionRuleListResponse {
  readonly items: readonly DimensionRule[];
}

/** `GET .../master-data/dimensions` and `.../dimension-values` */
export interface Dimension {
  readonly dimensionId: string;
  readonly code: string;
  readonly name: string;
  readonly status: string;
}

export interface DimensionValue {
  readonly dimensionValueId: string;
  readonly dimensionId: string;
  readonly code: string;
  readonly name: string;
  readonly status: string;
}

/** `GET .../finance/journal-templates` */
export interface JournalTemplate {
  readonly journalTemplateId: string;
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  readonly status: 'active' | 'inactive';
  readonly lines: readonly {
    readonly rowNumber: number;
    readonly accountId: string;
    readonly side: 'debit' | 'credit';
    readonly amountRial: string | null;
    readonly description: string | null;
  }[];
}

export interface JournalTemplateListResponse {
  readonly items: readonly JournalTemplate[];
}

/** `GET .../finance/journals/:id/attachments` */
export interface JournalAttachment {
  readonly journalAttachmentId: string;
  readonly journalId: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly sha256: string;
  readonly uploadedBy: string;
  readonly createdAt: string;
}

export interface JournalAttachmentListResponse {
  readonly items: readonly JournalAttachment[];
  readonly maxBytes: number;
}

/** `GET .../finance/reports/ledger` */
export interface LedgerResponse {
  readonly authority: 'posted';
  readonly account: { readonly code: string; readonly name: string } | null;
  readonly dimensionValue: {
    readonly dimensionValueId: string;
    readonly code: string;
    readonly name: string;
    readonly dimensionName: string;
  } | null;
  readonly fromDate: string | null;
  readonly toDate: string | null;
  readonly openingBalanceRial: string;
  readonly rows: readonly {
    readonly journalId: string;
    readonly documentNumber: string;
    readonly businessDate: string;
    readonly rowNumber: number;
    readonly accountRef: string;
    readonly accountName: string;
    readonly description: string | null;
    readonly debitRial: string;
    readonly creditRial: string;
    readonly balanceRial: string;
  }[];
  readonly totals: {
    readonly debitRial: string;
    readonly creditRial: string;
    readonly closingBalanceRial: string;
  };
}

export interface TrialBalanceColumnAmounts {
  readonly openingDebitRial: string;
  readonly openingCreditRial: string;
  readonly periodDebitRial: string;
  readonly periodCreditRial: string;
  readonly cumulativeDebitRial: string;
  readonly cumulativeCreditRial: string;
  readonly closingDebitRial: string;
  readonly closingCreditRial: string;
}

/** `GET .../finance/reports/trial-balance-columns` */
export interface TrialBalanceColumnsResponse {
  readonly authority: 'posted';
  readonly fromDate: string | null;
  readonly toDate: string | null;
  readonly levelSequence: number | null;
  readonly rows: readonly (TrialBalanceColumnAmounts & {
    readonly accountRef: string;
    readonly accountName: string;
    readonly levelSequence: number;
    readonly normalBalance: AccountNormalBalance;
    readonly contraryToNature: boolean;
  })[];
  readonly totals: TrialBalanceColumnAmounts;
  readonly balanced: boolean;
}

/** `GET …/finance/fiscal-years/:id/closing/preview` */
export interface ClosingJournalPlanDto {
  readonly kind: 'year_close_temporary' | 'year_close_permanent' | 'year_open';
  readonly businessDate: string;
  readonly fiscalYearId: string;
  readonly description: string;
  readonly debitTotalRial: string;
  readonly creditTotalRial: string;
  readonly lines: readonly {
    readonly accountId: string;
    readonly accountRef: string;
    readonly accountName: string;
    readonly dimensions: readonly {
      readonly dimensionId: string;
      readonly dimensionValueId: string;
      readonly dimensionName: string;
      readonly valueName: string;
    }[];
    readonly debitRial: string;
    readonly creditRial: string;
  }[];
}

export interface YearEndClosingPreviewResponse {
  readonly issues: readonly { readonly code: string; readonly message: string }[];
  readonly netIncomeRial: string;
  readonly temporaryRootDigits: readonly string[];
  readonly temporaryClosing: ClosingJournalPlanDto | null;
  readonly permanentClosing: ClosingJournalPlanDto | null;
  readonly opening: ClosingJournalPlanDto | null;
}

export interface YearEndClosingRunDto {
  readonly fiscalYearClosingId: string;
  readonly fiscalYearId: string;
  readonly nextFiscalYearId: string | null;
  readonly status: 'prepared' | 'rolled_back';
  readonly netIncomeRial: string;
  readonly preparedAt: string;
  readonly journals: readonly {
    readonly kind: string;
    readonly journalId: string;
    readonly status: string | null;
    readonly officialDocumentNumber: string | null;
  }[];
}

/** `GET …/finance/reports/income-statement` */
export interface IncomeStatementResponse {
  readonly fromDate: string;
  readonly toDate: string;
  readonly revenue: readonly { accountRef: string; accountName: string; amountRial: string }[];
  readonly expense: readonly { accountRef: string; accountName: string; amountRial: string }[];
  readonly totals: { revenueRial: string; expenseRial: string; netIncomeRial: string };
}

/** `GET …/finance/reports/balance-sheet` */
export interface BalanceSheetResponse {
  readonly asOfDate: string;
  readonly assets: readonly { accountRef: string; accountName: string; balanceRial: string }[];
  readonly liabilities: readonly { accountRef: string; accountName: string; balanceRial: string }[];
  readonly equity: readonly { accountRef: string; accountName: string; balanceRial: string }[];
  readonly currentPeriodNetIncomeRial: string;
  readonly totals: {
    assetsRial: string;
    liabilitiesRial: string;
    equityRial: string;
    equityWithCurrentPeriodRial: string;
  };
  readonly balanced: boolean;
}

export interface AccountingBook {
  readonly bookId: string;
  readonly code: string;
  readonly name: string;
  readonly kind: 'base' | 'statutory' | 'management' | 'tax' | 'consolidation';
  readonly parentBookId: string | null;
  readonly isStatutory: boolean;
  readonly series: string;
  readonly documentType: string;
  readonly cutoverDate: string | null;
  readonly baseCurrency: string;
  readonly status: 'active' | 'closed' | 'archived';
}

export interface AccountingBookListResponse {
  readonly items: readonly AccountingBook[];
  readonly total: number;
}

/** `GET …/finance/reports/cash-flow` */
export interface CashFlowLineDto {
  readonly title: string;
  readonly amountRial: string;
  readonly notes?: string | null;
}

export interface CashFlowStatementResponse {
  readonly authority: string;
  readonly kind: string;
  readonly fromDate: string;
  readonly toDate: string;
  readonly operatingActivities: readonly CashFlowLineDto[];
  readonly investingActivities: readonly CashFlowLineDto[];
  readonly financingActivities: readonly CashFlowLineDto[];
  readonly totals: {
    readonly netOperatingRial: string;
    readonly netInvestingRial: string;
    readonly netFinancingRial: string;
    readonly netCashFlowRial: string;
    readonly openingCashRial: string;
    readonly closingCashRial: string;
  };
  readonly reconciled: boolean;
}

export interface VatFilingItem {
  readonly filingId: string;
  readonly fiscalYearId: string;
  readonly quarter: number;
  readonly yearJalali: number;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly filingDeadline: string;
  readonly salesTaxableBaseRial: string;
  readonly salesVatRial: string;
  readonly purchaseTaxableBaseRial: string;
  readonly purchaseCreditRial: string;
  readonly payableVatRial: string;
  readonly status: 'draft' | 'submitted' | 'paid';
  readonly submissionReceiptNumber: string | null;
  readonly paymentReference: string | null;
  readonly submittedAt: string | null;
  readonly paidAt: string | null;
  readonly createdAt: string;
}

export interface VatFilingListResponse {
  readonly items: readonly VatFilingItem[];
}

export interface BankStatementLineDto {
  readonly statementLineId: string;
  readonly statementImportId: string;
  readonly rowNumber: number;
  readonly transactionDate: string;
  readonly referenceNumber: string | null;
  readonly description: string;
  readonly debitRial: string;
  readonly creditRial: string;
  readonly balanceRial: string;
  readonly isMatched: boolean;
  readonly matchedTreasuryTransactionId: string | null;
  readonly matchedAt: string | null;
  readonly matchedBy: string | null;
}

export interface BankStatementImportDto {
  readonly statementImportId: string;
  readonly bankAccountId: string;
  readonly fileName: string;
  readonly format: string;
  readonly fromDate: string;
  readonly toDate: string;
  readonly openingBalanceRial: string;
  readonly closingBalanceRial: string;
  readonly totalLinesCount: number;
  readonly matchedLinesCount: number;
  readonly status: string;
  readonly importedAt: string;
}

export interface BankReconciliationSessionDto {
  readonly sessionId?: string;
  readonly reconciliationId?: string;
  readonly bankAccountId: string;
  readonly statementImportId: string | null;
  readonly asOfDate: string;
  readonly bankStatementBalanceRial?: string;
  readonly statementBalanceRial?: string;
  readonly glBookBalanceRial?: string;
  readonly glBalanceRial?: string;
  readonly discrepancyRial?: string;
  readonly differenceRial?: string;
  readonly matchedCount?: number;
  readonly unmatchedCount?: number;
  readonly status: 'draft' | 'reconciled' | 'closed' | 'completed' | string;
  readonly notes: string | null;
  readonly createdAt: string;
}

export interface SalesReturnLineItem {
  readonly salesReturnLineId?: string;
  readonly rowNumber: number;
  readonly productId?: string | null;
  readonly description: string;
  readonly quantity: number | string;
  readonly unitPriceRial: string;
  readonly vatAmountRial?: string;
  readonly lineTotalRial?: string;
}

export interface SalesReturnRecord {
  readonly salesReturnId: string;
  readonly code: string;
  readonly partyId: string;
  readonly partyName?: string;
  readonly partyCode?: string;
  readonly originalOpenItemId: string | null;
  readonly originalInvoiceReference: string | null;
  readonly businessDate: string;
  readonly fiscalYearId: string;
  readonly fiscalPeriodId: string;
  readonly returnReason: string;
  readonly grossAmountRial: string;
  readonly discountAmountRial: string;
  readonly taxableBaseRial: string;
  readonly vatAmountRial: string;
  readonly totalAmountRial: string;
  readonly controlAccountId: string;
  readonly salesReturnAccountId: string;
  readonly vatAccountId: string | null;
  readonly journalId?: string;
  readonly originatingJournalId?: string;
  readonly journalNumber?: string | null;
  readonly journalStatus?: string | null;
  readonly status: 'draft' | 'posted' | 'cancelled';
  readonly restock: boolean;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly lines?: readonly SalesReturnLineItem[];
}

export interface SalesReturnListResponse {
  readonly items: readonly SalesReturnRecord[];
}
